import { Cart } from 'src/components/context';

/**
 * Cart-item `key` → the quantity the user means it to end up at, `0` meaning removal.
 *
 * The intent is stored, never the steps that produced it: pressing "−" twice then "+"
 * once collapses to a single change instead of three queued requests, and flushing is
 * then idempotent no matter how much the user fiddled before closing the drawer.
 */
export type PendingChanges = Record<string, number>;

export interface QuantityInput {
  key: string;
  quantity: number;
}

/**
 * WooCommerce hands the drawer two shapes of money: RAW numbers for the fields the
 * fragment asks for with `format: RAW` (shipping, discount, product price) and its own
 * formatted string for `total` and `contentsTotal` — "12,34&nbsp;€". Both have to parse.
 */
const parseMoney = (value?: string | null): number => {
  if (!value) {
    return 0;
  }

  const parsed = parseFloat(
    value
      .replace(/&nbsp;/g, '')
      .replace(/[^\d,.-]/g, '')
      .replace(',', '.')
  );

  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Woo's formatting for `total` / `contentsTotal`, reproduced so the optimistic values
 * flow through `Price` and the `cartTotal` parser in Cart.tsx exactly like server ones.
 */
export const formatMoney = (amount: number): string =>
  `${amount.toFixed(2).replace('.', ',')}&nbsp;€`;

/**
 * Recomputes everything in the cart that WooCommerce would have computed for us.
 *
 * Only the line items are genuinely known client-side, so shipping and discount are
 * carried over from whatever the server last said and corrected when the flush response
 * lands. Getting this wrong is visible: both progress bars in the drawer are derived
 * from `total`, and the header badge from `itemCount`.
 */
export const recalculateTotals = (cart: Cart): Cart => {
  const nodes = cart?.contents?.nodes ?? [];

  const itemCount = nodes.reduce(
    (count, node) => count + (node.quantity ?? 0),
    0
  );

  const contentsTotal = nodes.reduce(
    (sum, node) =>
      sum + parseMoney(node?.product?.node?.price) * (node.quantity ?? 0),
    0
  );

  const total =
    contentsTotal +
    parseMoney(cart.shippingTotal) +
    parseMoney(cart.shippingTax) -
    parseMoney(cart.discountTotal) -
    parseMoney(cart.discountTax);

  return {
    ...cart,
    contentsTotal: formatMoney(contentsTotal),
    total: formatMoney(Math.max(0, total)),
    contents: { ...cart.contents, itemCount, nodes },
  };
};

/**
 * The cart as it should look the instant the user presses "+", "−" or removes a line.
 * Object spreads keep every `__typename` in place, so the result stays valid both for
 * the Apollo cache and for the localStorage round-trip in AppProvider.
 */
export const applyQuantityChange = (
  cart: Cart,
  key: string,
  quantity: number
): Cart => {
  const nodes = (cart?.contents?.nodes ?? [])
    .map(node => (node.key === key ? { ...node, quantity } : node))
    .filter(node => (node.quantity ?? 0) > 0);

  return recalculateTotals({ ...cart, contents: { ...cart.contents, nodes } });
};

/**
 * `baseline` is the quantity the server is known to hold for that line. Landing back
 * on it drops the entry entirely — pressing "+" then "−" leaves the basket exactly as
 * the backend already has it, and sending a correction for that is a wasted request.
 */
export const mergePendingChange = (
  pending: PendingChanges,
  key: string,
  quantity: number,
  baseline?: number
): PendingChanges => {
  const next = Math.max(0, quantity);
  const merged = { ...pending };

  if (baseline === next) {
    delete merged[key];
  } else {
    merged[key] = next;
  }

  return merged;
};

export const toQuantityInputs = (pending: PendingChanges): QuantityInput[] =>
  Object.entries(pending).map(([key, quantity]) => ({ key, quantity }));

export const hasChanges = (pending: PendingChanges): boolean =>
  Object.keys(pending).length > 0;
